import React, { useEffect, useState, useMemo } from "react";
import {
  Card,
  Tabs,
  Descriptions,
  Tag,
  Button,
  Space,
  Spin,
  Alert,
  Modal,
  Form,
  Select,
  InputNumber,
  DatePicker,
  Empty,
  Tooltip,
} from 'antd';
import Table, { MAX_PAGE_SIZE } from 'components/Common/Table';
import {
  ArrowLeftOutlined,
  PauseCircleOutlined,
  PlayCircleOutlined,
  ReloadOutlined,
  ToolOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { useParams, useNavigate } from "react-router-dom";
import * as tmActions from "store/actions/TenantManagementActions";
import dayjs from "dayjs";

const STATUS_COLORS = {
  active: "green",
  pending: "orange",
  setting_up: "blue",
  failed: "red",
  suspended: "volcano",
  cancelled: "default",
};

const TenantDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [tenant, setTenant] = useState(null);
  const [subscriptions, setSubscriptions] = useState([]);
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersLoaded, setUsersLoaded] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [invoicesLoading, setInvoicesLoading] = useState(false);
  const [invoicesLoaded, setInvoicesLoaded] = useState(false);
  const [loading, setLoading] = useState(true);

  // Add-subscription modal state
  const [subModalOpen, setSubModalOpen] = useState(false);
  const [subForm] = Form.useForm();
  const [subPlans, setSubPlans] = useState([]);
  const [subInvoiceOptions, setSubInvoiceOptions] = useState([]);
  const [subInvoiceFetching, setSubInvoiceFetching] = useState(false);
  const [subSubmitting, setSubSubmitting] = useState(false);

  const fetchTenant = async () => {
    setLoading(true);
    const data = await tmActions.loadSyncedTenant(id);
    if (data) setTenant(data);
    setLoading(false);
  };

  const fetchSubscriptions = async () => {
    const data = await tmActions.loadSyncedSubscriptions({ synced_tenant: id, page_size: MAX_PAGE_SIZE });
    if (data) setSubscriptions(Array.isArray(data) ? data : data.results || []);
  };

  const fetchUsers = async () => {
    setUsersLoading(true);
    const data = await tmActions.loadTenantUsers(id);
    setUsers(Array.isArray(data?.users) ? data.users : []);
    setUsersLoaded(true);
    setUsersLoading(false);
  };

  const fetchInvoices = async () => {
    setInvoicesLoading(true);
    const data = await tmActions.loadTenantInvoices(id);
    setInvoices(Array.isArray(data?.invoices) ? data.invoices : []);
    setInvoicesLoaded(true);
    setInvoicesLoading(false);
  };

  const openAddSubscription = async () => {
    if (!tenant?.app_connection) return;
    subForm.resetFields();
    setSubInvoiceOptions([]);
    setSubModalOpen(true);
    // Lazy-load plans and the initial invoice list once on open.
    const [planList, invList] = await Promise.all([
      tmActions.loadOnboardingPlans(tenant.app_connection),
      tmActions.searchPaidInvoices(""),
    ]);
    setSubPlans(planList || []);
    setSubInvoiceOptions(
      (invList || []).map((i) => ({
        value: i.inv_number,
        label: `${i.inv_number} — ${i.account_name || "—"} — ${
          i.total_payable ?? i.total ?? 0
        }`,
      })),
    );
  };

  const debouncedFetchSubInvoices = useMemo(() => {
    let timer = null;
    return (q) => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(async () => {
        setSubInvoiceFetching(true);
        const list = await tmActions.searchPaidInvoices(q);
        setSubInvoiceFetching(false);
        setSubInvoiceOptions(
          (list || []).map((i) => ({
            value: i.inv_number,
            label: `${i.inv_number} — ${i.account_name || "—"} — ${
              i.total_payable ?? i.total ?? 0
            }`,
          })),
        );
      }, 300);
    };
  }, []);

  const handleAddSubscription = async () => {
    let values;
    try {
      values = await subForm.validateFields();
    } catch {
      return;
    }
    setSubSubmitting(true);
    try {
      await tmActions.addTenantSubscription(id, {
        plan: values.plan,
        amount: Number(values.amount || 0),
        end_date: values.end_date ? values.end_date.toISOString() : null,
        invoice_number: values.invoice_number || "",
      });
      setSubModalOpen(false);
      await fetchSubscriptions();
    } catch {
      // notification already raised by the action
    } finally {
      setSubSubmitting(false);
    }
  };

  useEffect(() => {
    fetchTenant();
    fetchSubscriptions();
  }, [id]);

  const handleSuspend = async () => {
    const result = await tmActions.suspendTenant(id);
    if (result) fetchTenant();
  };

  const handleActivate = async () => {
    const result = await tmActions.activateTenant(id);
    if (result) fetchTenant();
  };

  const handleRefreshStatus = async () => {
    const result = await tmActions.refreshTenantStatus(id);
    if (result) setTenant(result);
  };

  const handleRetrySetup = async () => {
    const result = await tmActions.retryTenantSetup(id);
    if (result) setTenant(result);
  };

  if (loading) {
    return <div style={{ padding: "16px", textAlign: "center" }}><Spin size="large" /></div>;
  }

  if (!tenant) {
    return <div style={{ padding: "16px" }}><Alert message="Tenant not found" type="error" /></div>;
  }

  const userColumns = [
    { title: "Username", dataIndex: "username", key: "username" },
    { title: "Email", dataIndex: "email", key: "email" },
    {
      title: "Name",
      key: "name",
      render: (_, u) =>
        [u.first_name, u.last_name].filter(Boolean).join(" ") || "-",
    },
    {
      title: "Active",
      dataIndex: "is_active",
      key: "is_active",
      render: (a) => <Tag color={a ? "green" : "default"}>{a ? "Yes" : "No"}</Tag>,
    },
    {
      title: "Role",
      key: "role",
      render: (_, u) => {
        if (u.is_superuser) return <Tag color="volcano">superuser</Tag>;
        if (u.is_staff) return <Tag color="blue">staff</Tag>;
        return <Tag>member</Tag>;
      },
    },
    {
      title: "Last Login",
      dataIndex: "last_login",
      key: "last_login",
      render: (d) => (d ? dayjs(d).format("YYYY-MM-DD HH:mm") : "-"),
    },
  ];

  const subColumns = [
    { title: "Plan", dataIndex: "plan_name", key: "plan_name" },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (s) => <Tag color={s === "active" ? "green" : "default"}>{s}</Tag>,
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      render: (a) => `₹${parseFloat(a).toLocaleString()}`,
    },
    { title: "Start", dataIndex: "start_date", key: "start_date" },
    { title: "End", dataIndex: "end_date", key: "end_date" },
  ];

  const invoiceColumns = [
    {
      title: "Invoice #",
      dataIndex: "inv_number",
      key: "inv_number",
      render: (n, row) => (
        <a href={`/invoices/edit/${row.id}`} target="_blank" rel="noreferrer">{n}</a>
      ),
    },
    { title: "Account", dataIndex: "account_name", key: "account_name", render: (v) => v || "-" },
    {
      title: "Date",
      dataIndex: "invoice_date",
      key: "invoice_date",
      render: (d) => (d ? dayjs(d).format("YYYY-MM-DD") : "-"),
    },
    {
      title: "Due",
      dataIndex: "due_date",
      key: "due_date",
      render: (d) => (d ? dayjs(d).format("YYYY-MM-DD") : "-"),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (s) => (
        <Tag color={s === "PAID" ? "green" : s === "DRAFT" ? "default" : "blue"}>{s}</Tag>
      ),
    },
    {
      title: "Total",
      dataIndex: "total_payable",
      key: "total_payable",
      render: (t, row) => {
        const amount = t ?? row.total ?? 0;
        const cur = row.currency_code || "";
        return cur ? `${cur} ${parseFloat(amount).toLocaleString()}` : `₹${parseFloat(amount).toLocaleString()}`;
      },
    },
  ];

  const tabItems = [
    {
      key: "overview",
      label: "Overview",
      children: (
        <Descriptions bordered column={2}>
          <Descriptions.Item label="Name">{tenant.name}</Descriptions.Item>
          <Descriptions.Item label="App">
            <Tag>{tenant.app_connection_name}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Schema"><code>{tenant.schema_name}</code></Descriptions.Item>
          <Descriptions.Item label="Domain">{tenant.domain || "-"}</Descriptions.Item>
          <Descriptions.Item label="Status">
            <Tag color={STATUS_COLORS[tenant.status]}>{tenant.status}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Admin Email">{tenant.admin_email}</Descriptions.Item>
          <Descriptions.Item label="Paid Until">
            {tenant.paid_until ? dayjs(tenant.paid_until).format("YYYY-MM-DD") : "-"}
          </Descriptions.Item>
          <Descriptions.Item label="On Trial">
            {tenant.on_trial ? <Tag color="blue">Yes</Tag> : "No"}
          </Descriptions.Item>
          <Descriptions.Item label="External ID"><code>{tenant.external_id}</code></Descriptions.Item>
          <Descriptions.Item label="Last Synced">
            {dayjs(tenant.last_synced_at).format("YYYY-MM-DD HH:mm:ss")}
          </Descriptions.Item>
        </Descriptions>
      ),
    },
    {
      key: "subscriptions",
      label: `Subscriptions (${subscriptions.length})`,
      children: (
        <>
          <Space style={{ marginBottom: 12, justifyContent: "flex-end", display: "flex" }}>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={openAddSubscription}
              disabled={!tenant.external_id || tenant.status === "suspended"}
            >
              Add Subscription
            </Button>
          </Space>
          <Table
            dataSource={subscriptions}
            columns={subColumns}
            rowKey="id"
            pagination={{ defaultPageSize: 10, showSizeChanger: true }}
          />
        </>
      ),
    },
    {
      key: "users",
      label: usersLoaded ? `Users (${users.length})` : "Users",
      children: (
        <>
          {!tenant.external_id && (
            <Alert
              type="warning"
              message="No external_id yet — users can't be fetched until provisioning completes."
              style={{ marginBottom: 12 }}
            />
          )}
          {tenant.external_id && (
            <Table
              dataSource={users}
              columns={userColumns}
              rowKey="id"
              loading={usersLoading}
              pagination={{ defaultPageSize: 10, showSizeChanger: true }}
              locale={{
                emptyText: usersLoaded
                  ? "No users found for this tenant on the remote app."
                  : "Loading…",
              }}
            />
          )}
        </>
      ),
    },
    {
      key: "invoices",
      label: invoicesLoaded ? `Invoices (${invoices.length})` : "Invoices",
      children: (
        <>
          {!tenant.external_id && (
            <Alert
              type="warning"
              message="No external_id yet — invoices can't be fetched until provisioning completes."
              style={{ marginBottom: 12 }}
            />
          )}
          {tenant.external_id && (
            <Table
              dataSource={invoices}
              columns={invoiceColumns}
              rowKey="id"
              loading={invoicesLoading}
              pagination={{ defaultPageSize: 10, showSizeChanger: true }}
              locale={{
                emptyText: invoicesLoaded
                  ? "No TesonCRM invoices linked to this tenant yet."
                  : "Loading…",
              }}
            />
          )}
        </>
      ),
    },
    {
      key: "raw_data",
      label: "Raw Data",
      children: (
        <Card size="small">
          <pre style={{ maxHeight: 500, overflow: "auto", fontSize: 12 }}>
            {JSON.stringify(tenant.remote_data, null, 2)}
          </pre>
        </Card>
      ),
    },
  ];

  return (
    <div style={{ padding: "16px" }}>
      <Space style={{ marginBottom: 16 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate("/tenant-management/tenants")}>
          Back
        </Button>
        <span style={{ fontSize: 18, fontWeight: 600 }}>
          {tenant.name}
          <Tag color={STATUS_COLORS[tenant.status]} style={{ marginLeft: 8 }}>
            {tenant.status}
          </Tag>
        </span>
      </Space>

      <Space style={{ marginBottom: 16, display: "flex", justifyContent: "flex-end" }}>
        {["setting_up", "pending", "failed"].includes(tenant.status) && (
          <Tooltip title="Refresh Status">
            <Button icon={<ReloadOutlined />} onClick={handleRefreshStatus} aria-label="Refresh Status" />
          </Tooltip>
        )}
        {["failed", "setting_up"].includes(tenant.status) && (
          <Button type="primary" danger icon={<ToolOutlined />} onClick={handleRetrySetup}>
            Retry Setup
          </Button>
        )}
        {tenant.status === "active" && (
          <Button danger icon={<PauseCircleOutlined />} onClick={handleSuspend}>
            Suspend Tenant
          </Button>
        )}
        {tenant.status === "suspended" && (
          <Button type="primary" icon={<PlayCircleOutlined />} onClick={handleActivate}>
            Activate Tenant
          </Button>
        )}
      </Space>

      <Card>
        <Tabs
          items={tabItems}
          onChange={(key) => {
            if (key === "users" && !usersLoaded && !usersLoading && tenant.external_id) {
              fetchUsers();
            }
            if (
              key === "invoices" &&
              !invoicesLoaded &&
              !invoicesLoading &&
              tenant.external_id
            ) {
              fetchInvoices();
            }
          }}
        />
      </Card>

      <Modal
        title={`Add Subscription for ${tenant.name}`}
        open={subModalOpen}
        onCancel={() => setSubModalOpen(false)}
        onOk={handleAddSubscription}
        confirmLoading={subSubmitting}
        okText="Create Subscription"
        destroyOnClose
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message="Creating a new active subscription will demote any currently active subscription to inactive."
        />
        <Form form={subForm} layout="vertical">
          <Form.Item
            name="plan"
            label="Plan"
            rules={[{ required: true, message: "Pick a plan" }]}
          >
            <Select
              options={(subPlans || []).map((p) => ({
                value: p.id,
                label: `${p.name} — ₹${p.price}`,
              }))}
              onChange={(v) => {
                const p = (subPlans || []).find((pp) => pp.id === v);
                if (p) subForm.setFieldsValue({ amount: Number(p.price || 0) });
              }}
            />
          </Form.Item>
          <Form.Item
            name="amount"
            label="Amount"
            rules={[{ required: true, message: "Enter amount" }]}
          >
            <InputNumber min={0} step={0.01} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item
            name="end_date"
            label="End Date"
            rules={[{ required: true, message: "Pick subscription end date" }]}
          >
            <DatePicker showTime style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item
            name="invoice_number"
            label="TesonCRM Invoice"
            rules={[{ required: true, message: "Pick a paid invoice" }]}
          >
            <Select
              showSearch
              allowClear
              filterOption={false}
              loading={subInvoiceFetching}
              options={subInvoiceOptions}
              placeholder="Search invoice number or account"
              onSearch={(q) => debouncedFetchSubInvoices(q)}
              notFoundContent={
                subInvoiceFetching ? <Spin size="small" /> : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
              }
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default TenantDetail;
